import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IntlService } from '@progress/kendo-angular-intl';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReferenceDataContextualHelps } from '../../../../../lib/constants/reference-data-contextual-helps';
import { ReferenceDataLabels } from '../../../../../lib/constants/reference-data-labels';
import { ReferenceDataOptionLists } from '../../../../../lib/constants/reference-data-option-lists';
import { Product } from '../../../../../lib/models/api/product.model';
import { ReferenceData } from '../../../../../lib/models/reference-data.model';
import { ReferenceDataService } from '../../../../../lib/services/reference-data.service';
import { ProductSpecialFeatureDto } from '../../../../../product-picker-controls/models/product-special-feature-dto.model';
import { ApplicationFormStore } from '../../../../shared/services/application-form-store';
import { ReferenceDataConstants } from './../../../../../lib/constants/reference-data-constants';
import { CaseProduct } from './../../../../mortgage-application/models/case-product.model';
import { FormProductService } from './../../../services/form-product.service';

@Directive()
export abstract class FormProductSelectionComponent implements OnDestroy {
  constructor(
    protected cdr: ChangeDetectorRef,
    protected applicationFormStore: ApplicationFormStore,
    protected productService: FormProductService,
    protected referenceDataService: ReferenceDataService,
    protected intlService: IntlService,
    protected sanitizer: DomSanitizer
  ) {}
  @Input() public isDisabled?: boolean;
  @Input() public allowsExpiredProduct: boolean;
  @Output() public readonly productChange = new EventEmitter();

  public referenceDataOptionLists = ReferenceDataOptionLists;
  public referenceDataLabels = ReferenceDataLabels;
  public referenceDataContextualHelps = ReferenceDataContextualHelps;

  public caseProduct: CaseProduct;

  public loanTypes: Array<ReferenceData>;
  public selectedLoanType: string;

  public isProductCodeInvalid: boolean;
  public productCode: string;
  public productList: Array<Product>;
  public isProductSpecialFeatureDialogOpen: boolean;

  public productWarningMessage: string;

  public hasErrorProductCode: boolean;
  public hasErrorProductName: boolean;
  public hasErrorProductType: boolean;

  protected allSpecialFeatures: Array<ProductSpecialFeatureDto>;
  protected caseNumberAsHash: string;

  public isBuyToLet: boolean;
  public isBrokerCase: boolean;
  public isAffordabilityCalculatorOpened: boolean;
  public affordabilityCalculatorUrl: SafeResourceUrl;

  protected destroySubject$ = new Subject();

  public abstract selectionType: string;
  protected abstract getProductByProductCode(): Observable<Product>;
  protected abstract getProductsByLoanType(): Observable<Array<Product>>;
  protected abstract getProductWarningMessage(): string;
  protected abstract setFormApplicationLoanTypeCode(): void;

  public get product(): Product {
    return this.caseProduct ? this.caseProduct.Product : undefined;
  }

  public get selectedProductId(): number {
    return this.product ? this.product.ProductID : undefined;
  }

  public abstract get isPartAndPartRepaymentType(): boolean;
  public abstract get partAndPartRepaymentCaseProduct(): CaseProduct;
  public resetSolicitorDetails(): void {}

  public set selectedProductId(value: number) {
    const productHasChanged = this.caseProduct?.ProductID !== value;

    if (!value) {
      this.resetCurentProduct();
    } else {
      const selectedProduct = this.productList.find((product) => product.ProductID === value);
      this.setCurrentProduct(selectedProduct);
    }

    if (productHasChanged) {
      this.productChange.emit();
    }

    this.isProductCodeInvalid = false;
  }

  protected initialize(): void {
    this.initializeLoanTypes();
    this.productService
      .getProductsSpecialFeature()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((data) => (this.allSpecialFeatures = data));
  }

  protected initializeProductRelatedFields(): void {
    if (this.product) {
      this.productCode = this.product.ProductCode;
      this.selectedLoanType = this.product.LoanTypeCode;
      this.onUpdatedLoanType(false);
    } else {
      this.selectedLoanType = undefined;
      this.removeProductAndProductList();
      this.resetCurentProduct();
    }
  }

  protected setCurrentProduct(product: Product): void {
    if (!this.product || this.product.ProductID !== product.ProductID) {
      delete (product as any).$id;
      this.caseProduct.Product = product;
      this.caseProduct.ProductID = product.ProductID;
      this.productCode = this.product.ProductCode;
      this.selectedLoanType = this.product.LoanTypeCode;

      if (this.isPartAndPartRepaymentType) {
        const partAndPartRepaymentCaseProduct = this.partAndPartRepaymentCaseProduct;
        partAndPartRepaymentCaseProduct.Product = product;
        partAndPartRepaymentCaseProduct.ProductID = product.ProductID;
      }

      this.initializeProductSpecialFeatures();
      this.setFormApplicationLoanTypeCode();
    }
  }

  private resetCurentProduct(): void {
    this.caseProduct.Product = undefined;
    this.caseProduct.ProductID = undefined;
    this.productCode = undefined;

    if (this.isPartAndPartRepaymentType) {
      const partAndPartRepaymentCaseProduct = this.partAndPartRepaymentCaseProduct;
      partAndPartRepaymentCaseProduct.Product = undefined;
      partAndPartRepaymentCaseProduct.ProductID = undefined;
    }
  }

  protected removeSelectedProductAndKeepProductCode(): void {
    this.caseProduct.Product = undefined;
    this.caseProduct.ProductID = undefined;
    this.isProductCodeInvalid = this.productCode && this.productCode.length !== 0;
  }

  protected isNewProductSelected(): boolean {
    return !this.product || this.productCode !== this.product.ProductCode;
  }

  protected selectNewProduct(): void {
    if (this.isNewProductSelected() && !this.allowsExpiredProduct) {
      this.getProductByProductCode()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          (product) => {
            this.setCurrentProduct(product);
            this.initializeProductRelatedFields();
            this.productChange.emit();
          },
          () => {
            this.removeSelectedProductAndKeepProductCode();
            this.cdr.detectChanges();
          }
        );
    }
  }

  protected setProductExpirationFlags(): void {
    if (this.product) {
      this.productService
        .getProductExpirationWarningInterval(this.caseNumberAsHash)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe((interval) => {
          this.setExpirationFlags(this.caseProduct.Product, interval);
          this.productWarningMessage = this.getProductWarningMessage();
          this.cdr.detectChanges();
        });
    }
  }

  public setExpirationFlags(product: Product, interval: number): void {
    if (product.WithdrawDate) {
      const withdrawDate = new Date(product.WithdrawDate);
      const today = new Date();
      let isProductWithdrawn = false;
      if (today > withdrawDate) {
        product.IsWithdrawn = true;
        isProductWithdrawn = true;
      }

      if (!isProductWithdrawn) {
        today.setDate(today.getDate() + interval);
        if (withdrawDate < today) {
          product.IsAboutToExpire = true;
        }
      }
    }
  }

  private initializeLoanTypes(): void {
    this.loanTypes = new Array<ReferenceData>();
    this.applicationFormStore.availableLoanTypes.forEach((value) =>
      value.forEach((loanType) => this.loanTypes.push(loanType))
    );
  }

  private initializeProductSpecialFeatures(): void {
    if (this.product.ProductSpecialFeatures.length > 0) {
      this.product.ProductSpecialFeatures.forEach((productSpecialFeature) => {
        productSpecialFeature.Value = this.allSpecialFeatures.find(
          (feature) => feature.SpecialFeatureName === productSpecialFeature.SpecialFeatureName
        ).Value;
      });
      this.isProductSpecialFeatureDialogOpen = true;
    }
  }

  private removeProductAndProductList(): void {
    this.selectedProductId = undefined;
    this.productList = [];
  }

  public onBlurProductCode(): void {
    if (this.selectionType === this.referenceDataOptionLists.ProductSelectionByCode) {
      this.isProductCodeInvalid = false;
      if (this.productCode) {
        this.selectNewProduct();
      } else {
        this.removeSelectedProductAndKeepProductCode();
      }
    }
  }

  public onUpdatedLoanType(resetProduct: boolean): void {
    if (!this.selectedLoanType) {
      this.removeProductAndProductList();
      this.resetSolicitorDetails();

      return;
    }

    this.getProductsByLoanType()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        (data) => {
          if (resetProduct) {
            this.selectedProductId = undefined;
          }
          this.productList = data;
          this.cdr.detectChanges();
        },
        () => {
          this.removeProductAndProductList();
          this.cdr.detectChanges();
        }
      );
  }

  public cancelProductSpecialFeatures(): void {
    this.selectedProductId = undefined;
  }

  public getProductStatusWarning(product: Product, withdrawLabel: string, expireLabel: string): string {
    const message = product.IsWithdrawn ? withdrawLabel : expireLabel;
    const formattedWithdrawDate = this.intlService.formatDate(new Date(product.WithdrawDate), 'dd/MM/yyyy HH:mm:ss');
    const formattedClosedDate = this.intlService.formatDate(new Date(product.DateClosed), 'dd/MM/yyyy HH:mm:ss');
    const label = this.referenceDataService
      .getLocalizeLabel(message)
      .replace('{0}', formattedWithdrawDate)
      .replace('{1}', formattedClosedDate);

    return label;
  }

  public openAffordabilityCalculator($event: any): void {
    if ($event.target.tagName !== 'A') {
      return;
    }
    const unsafeUrl = this.referenceDataService.getClientConfigurationValue(
      ReferenceDataConstants.BTLAffordabilityCalculatorUrl
    );
    this.affordabilityCalculatorUrl = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
    this.isAffordabilityCalculatorOpened = true;
  }

  public ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
